import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Mic, FileText, MessageSquare, Globe } from "lucide-react";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=in.classbridge.app";
const QR_IMG_URL = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(PLAY_STORE_URL)}&bgcolor=ffffff&color=1a1918&margin=4`;

const PlayStoreBadge = ({ className }) => (
  <svg viewBox="0 0 135 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect width="135" height="40" rx="5" fill="#1a1918" />
    <rect x="0.5" y="0.5" width="134" height="39" rx="4.5" stroke="white" strokeOpacity="0.3" />
    <text x="48" y="14" fill="white" fontSize="8" fontFamily="sans-serif" opacity="0.7">GET IT ON</text>
    <text x="48" y="28" fill="white" fontSize="14" fontFamily="sans-serif" fontWeight="bold">Google Play</text>
    <path d="M30 20L22 28V12L30 20Z" fill="#E8542A" />
    <path d="M22 12L30 20L26 24L18 16L22 12Z" fill="#4FC3F7" />
    <path d="M22 28L30 20L26 16L18 24L22 28Z" fill="#A5D6A7" />
    <path d="M26 24L30 20L26 16" fill="#FF6F00" stroke="none" />
  </svg>
);


const BridgeMark = ({ className }) => (
  <svg viewBox="0 0 36 28" fill="none" className={className} aria-hidden="true">
    <rect x="1.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <rect x="31.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <path d="M3 13 Q18 1 33 13" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <line x1="0" y1="21" x2="36" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="10.5" y1="7" x2="10.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="18" y1="3" x2="18" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="25.5" y1="7" x2="25.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const MARQUEE_ITEMS = [
  "Record", "Transcribe", "English", "हिंदी", "मराठी",
  "Generate", "Share", "ClassBridge", "WhatsApp", "Automate",
  "Record", "Transcribe", "English", "हिंदी", "मराठी",
  "Generate", "Share", "ClassBridge", "WhatsApp", "Automate",
];

const FEATURES = [
  {
    icon: Mic,
    tag: "Record",
    title: "Capture every class",
    body: "One tap to start. Record as long as your class runs — we auto-split long sessions behind the scenes so nothing is lost.",
    accent: "#E8542A",
    span: "col-span-2",
    bg: "#0D0C0B",
    light: false,
  },
  {
    icon: Globe,
    tag: "Multilingual",
    title: "3 languages, zero setup",
    body: "English · हिंदी · मराठी",
    accent: "#E8A25A",
    span: "col-span-1",
    bg: "#F3EFEA",
    light: true,
  },
  {
    icon: FileText,
    tag: "Transcribe",
    title: "AI transcription in seconds",
    body: "OpenAI Whisper converts your recording to text — even through classroom noise, accents and mixed languages.",
    accent: "#1A1918",
    span: "col-span-1",
    bg: "#FDF3EC",
    light: true,
  },
  {
    icon: MessageSquare,
    tag: "Share",
    title: "WhatsApp-ready parent updates",
    body: "AI drafts a polished message with school header, lesson summary, homework reminder and your personal teacher sign-off.",
    accent: "#E8542A",
    span: "col-span-2",
    bg: "#E8542A",
    light: false,
  },
];

const STEPS = [
  { n: "01", label: "Record", desc: "Tap mic, teach your class." },
  { n: "02", label: "Transcribe", desc: "AI converts audio to text in your language." },
  { n: "03", label: "Share", desc: "Send the AI-crafted update to parents on WhatsApp." },
];

function Nav({ scrolled }) {
  return (
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className={`fixed left-0 right-0 top-0 z-50 flex items-center justify-between px-6 py-4 transition-all duration-500 md:px-12 ${
        scrolled ? "bg-[#FDF3EC]/95 shadow-sm backdrop-blur-sm" : "bg-transparent"
      }`}
    >
      <Link to="/" className={`flex items-center gap-2.5 transition-colors duration-300 ${scrolled ? "text-[#E8542A]" : "text-[#E8542A]"}`}>
        <BridgeMark className="h-6 w-8" />
        <span className="font-display text-xl font-bold tracking-tight">ClassBridge</span>
      </Link>
      <Link
        to="/login"
        data-testid="nav-sign-in"
        className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 ${
          scrolled
            ? "bg-[#E8542A] text-white hover:bg-[#C94520]"
            : "border border-white/30 text-white hover:bg-white/10"
        }`}
      >
        Sign in <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </motion.nav>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const lines = ["Classroom to", "parents,", "every day."];

  return (
    <section
      ref={ref}
      data-testid="home-hero"
      className="relative flex min-h-screen flex-col items-start justify-center overflow-hidden bg-[#0D0C0B] px-6 pb-20 pt-28 md:px-16 lg:px-24"
    >
      {/* Grain overlay */}
      <div
        className="pointer-events-none absolute inset-0 z-10 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "128px",
        }}
      />
      {/* Warm glow */}
      <div className="pointer-events-none absolute left-0 top-1/3 h-[600px] w-[600px] -translate-x-1/4 rounded-full bg-[#E8542A] opacity-[0.08] blur-[120px]" />

      <motion.div style={{ y, opacity }} className="relative z-20 w-full max-w-5xl">
        {/* Label */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-8 flex items-center gap-2"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E8542A]/40 bg-[#E8542A]/10 px-3 py-1 text-xs font-semibold tracking-widest text-[#E8A25A] uppercase">
            <span className="h-1.5 w-1.5 rounded-full bg-[#E8542A]" />
            AI Teaching Companion
          </span>
        </motion.div>

        {/* Headline — masked line reveal */}
        <h1 className="font-display text-[clamp(3rem,8vw,7rem)] font-bold leading-[0.95] tracking-tight">
          {lines.map((line, i) => (
            <div key={line} className="overflow-hidden">
              <motion.span
                className={`block ${i === 2 ? "text-[#E8542A]" : "text-[#F5F0EB]"}`}
                initial={{ y: "105%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.77, 0, 0.175, 1] }}
              >
                {line}
              </motion.span>
            </div>
          ))}
        </h1>

        {/* Sub */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-8 max-w-md text-base leading-relaxed text-[#8A7A72] md:text-lg"
        >
          Record your class. AI transcribes it. Parents get a beautiful update on WhatsApp — in 30 seconds.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.0 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/login"
            data-testid="sign-in-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-[#E8542A] px-8 py-3.5 text-sm font-bold text-white transition-all duration-200 hover:bg-[#C94520] hover:gap-3"
          >
            Start teaching
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <span className="text-xs text-[#5C4E46]">Free to start · No credit card</span>
        </motion.div>
      </motion.div>

      {/* Floating recording indicator */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.3 }}
        className="absolute bottom-16 right-6 z-20 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm md:right-16"
      >
        <div className="relative flex h-3 w-3">
          <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
        </div>
        <div>
          <p className="text-xs font-semibold text-white">Recording</p>
          <p className="text-[10px] text-white/50">Class running · 47:23</p>
        </div>
        <div className="ml-1 flex items-end gap-[3px]">
          {[4, 12, 7, 18, 5, 14, 9, 16, 6].map((h, i) => (
            <div
              key={h}
              className="w-[3px] rounded-full bg-[#E8542A]/70"
              style={{
                height: `${h}px`,
                animation: `waveform ${0.8 + i * 0.1}s ease-in-out infinite alternate`,
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-1"
      >
        <div className="h-8 w-[1px] bg-gradient-to-b from-white/30 to-transparent" />
      </motion.div>
    </section>
  );
}

function Marquee() {
  return (
    <div className="overflow-hidden border-y border-white/10 bg-[#0D0C0B] py-3.5">
      <div className="flex animate-marquee items-center gap-10 whitespace-nowrap">
        {MARQUEE_ITEMS.map((item, i) => (
          <span
            key={`${item}-${i}`}
            className={`text-xs font-bold uppercase tracking-[0.2em] ${
              item === "ClassBridge" ? "text-[#E8542A]" : "text-white/30"
            }`}
          >
            {item}
            <span className="ml-10 text-white/10">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Features() {
  return (
    <section className="bg-[#FDF3EC] px-6 py-20 md:px-12 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-[#E8542A]">Features</p>
          <h2 className="font-display mt-2 text-4xl font-bold text-[#1A1918] md:text-5xl">
            Everything a teacher needs.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                className={`group relative overflow-hidden rounded-3xl p-7 ${f.span} transition-transform duration-300 hover:-translate-y-1`}
                style={{ backgroundColor: f.bg }}
              >
                <div
                  className="mb-5 flex h-10 w-10 items-center justify-center rounded-2xl"
                  style={{ backgroundColor: `${f.accent}22` }}
                >
                  <Icon className="h-5 w-5" style={{ color: f.accent }} />
                </div>
                <p
                  className="mb-1 text-xs font-bold uppercase tracking-widest"
                  style={{ color: `${f.accent}99` }}
                >
                  {f.tag}
                </p>
                <h3
                  className="font-display text-xl font-bold"
                  style={{ color: f.light ? "#1A1918" : "#F5F0EB" }}
                >
                  {f.title}
                </h3>
                <p
                  className="mt-2 text-sm leading-relaxed"
                  style={{ color: f.light ? "#8A6D5C" : "#A8968E" }}
                >
                  {f.body}
                </p>
                {/* Subtle corner glow */}
                <div
                  className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
                  style={{ backgroundColor: f.accent }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bg-[#F3EFEA] px-6 py-20 md:px-12 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-14 text-center"
        >
          <p className="text-xs font-bold uppercase tracking-widest text-[#E8542A]">How it works</p>
          <h2 className="font-display mt-2 text-4xl font-bold text-[#1A1918] md:text-5xl">
            Three steps, zero friction.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.12 }}
              className="relative rounded-3xl border border-black/5 bg-white p-8"
            >
              <span className="font-display text-6xl font-bold text-[#E8542A]/15">{s.n}</span>
              <h3 className="font-display mt-2 text-2xl font-bold text-[#1A1918]">{s.label}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#8A6D5C]">{s.desc}</p>
              {i < STEPS.length - 1 && (
                <div className="absolute -right-3 top-1/2 hidden -translate-y-1/2 md:block">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M13 6l6 6-6 6" stroke="#E8542A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LanguageStrip() {
  const langs = [
    { script: "English", sub: "Roman" },
    { script: "हिंदी", sub: "Devanagari" },
    { script: "मराठी", sub: "Devanagari" },
  ];
  return (
    <section className="overflow-hidden bg-[#0D0C0B] py-16 px-6 md:px-12">
      <div className="mx-auto max-w-6xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8 text-center text-xs font-bold uppercase tracking-widest text-white/30"
        >
          Languages supported
        </motion.p>
        <div className="flex items-center justify-center gap-6 md:gap-16">
          {langs.map((l, i) => (
            <motion.div
              key={l.script}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-center"
            >
              <p className="font-display text-4xl font-bold text-white md:text-5xl">{l.script}</p>
              <p className="mt-1 text-xs text-white/30">{l.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GetTheApp() {
  return (
    <section data-testid="get-the-app-section" className="bg-[#0D0C0B] px-6 py-20 md:px-12 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-center gap-12 md:flex-row md:items-center md:gap-20">
          {/* Left text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="flex-1"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-[#E8542A]">Mobile App</p>
            <h2 className="font-display mt-3 text-4xl font-bold leading-tight text-[#F5F0EB] md:text-5xl">
              Take ClassBridge<br />everywhere you teach
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#8A7A72]">
              Record classes, get transcripts and send parent updates right from your phone. Free on Android.
            </p>
            <a
              data-testid="play-store-link"
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-block"
            >
              <PlayStoreBadge className="h-12 w-40 transition-opacity duration-200 hover:opacity-80" />
            </a>
          </motion.div>

          {/* Right QR */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="rounded-3xl border border-white/10 bg-white p-5 shadow-2xl shadow-black/50">
              <img
                data-testid="playstore-qr-code"
                src={QR_IMG_URL}
                alt="Scan to download ClassBridge on Android"
                width={180}
                height={180}
                className="block rounded-xl"
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-white/70">Scan to download</p>
              <p className="mt-0.5 text-xs text-white/30">Works on any Android phone</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}


function CTA() {
  return (
    <section className="bg-[#E8542A] px-6 py-24 md:px-12">
      <div className="mx-auto max-w-3xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-4xl font-bold text-white md:text-5xl lg:text-6xl">
            Transform your classroom communication.
          </h2>
          <p className="mt-6 text-base text-white/70 md:text-lg">
            Thousands of teachers across India use ClassBridge every day. Join them.
          </p>
          <Link
            to="/login"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-10 py-4 text-sm font-bold text-[#E8542A] transition-all duration-200 hover:bg-[#FDF3EC] hover:gap-3"
          >
            Get started free
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#0D0C0B]">
      <Nav scrolled={scrolled} />
      <Hero />
      <Marquee />
      <Features />
      <HowItWorks />
      <LanguageStrip />
      <GetTheApp />
      <CTA />
      <footer className="border-t border-white/10 bg-[#0D0C0B] px-6 py-6 md:px-12">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2 text-[#E8542A]">
            <BridgeMark className="h-5 w-7" />
            <span className="font-display text-base font-bold">ClassBridge</span>
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="text-xs text-white/30 transition-colors hover:text-white/60"
            >
              Contact
            </Link>
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-xs text-white/30 transition-colors hover:text-white/60"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
